import React, { useState } from "react";
import {
  AppShell,
  Collapse,
  Group,
  ScrollArea,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";

import NavbarLink from "./NavbarLink";
import { EventSwitcher } from "./EventSwitcher";
import classes from "./Navbar.module.css";
import { filteredSections } from "./links";
import { getCurrentWorkspace } from "../../services/userConfig";
import { useGetAllFeatureFlags } from "../../api/featureFlags";
import { useGetEventSetupProgress } from "../../api/event";
import { useGetMobileMetadata } from "../../api/systemSettings";

function Navbar({ opened, toggle, user, collapsed }) {
  // Check user role. If user role is wsAdmin, check user.config.isTicketAdmin
  // If user.config.isTicketAdmin is true, set role to ticketAdmin
  const role =
    user?.role === "wsAdmin" && user?.config?.isTicketAdmin
      ? "ticketAdmin"
      : user?.role;

  const currentWorkspace = getCurrentWorkspace(user);

  const { data: featureFlags } = useGetAllFeatureFlags({
    eventId: currentWorkspace.eventId,
  });

  const { data: systemSettings } = useGetMobileMetadata();
  const showSetup = systemSettings?.enableSetupScreen === true;

  const isAdmin = role === "sysAdmin" || role === "wsAdmin";
  const { data: setupProgress } = useGetEventSetupProgress(
    isAdmin && showSetup ? currentWorkspace.eventId : null
  );
  const setupBadge =
    setupProgress &&
    `${setupProgress.totals.done}/${setupProgress.totals.total}`;

  const sections = filteredSections(role, featureFlags, systemSettings);

  // Initialize all sections as open
  const [openSections, setOpenSections] = useState(() => {
    const initial = {};
    sections?.forEach((section) => {
      initial[section.id] = true;
    });
    return initial;
  });

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  return (
    <AppShell.Navbar
      hidden={!opened}
      className={`${classes.navbar} ${collapsed ? classes.collapsed : ""}`}
    >
      <EventSwitcher collapsed={collapsed} />
      <ScrollArea className={classes.navbarMain}>
        <div className={classes.navbarMainContent}>
          {sections?.map((section) => (
            <div key={section.id} className={classes.section}>
              {!collapsed && (
                <UnstyledButton
                  onClick={() => toggleSection(section.id)}
                  className={classes.sectionHeader}
                >
                  <Group justify="space-between" wrap="nowrap">
                    <Text className={classes.sectionLabel}>
                      {section.label}
                    </Text>
                    <IconChevronRight
                      className={classes.sectionChevron}
                      size={14}
                      stroke={1.5}
                      style={{
                        transform: openSections[section.id]
                          ? "rotate(90deg)"
                          : "none",
                      }}
                    />
                  </Group>
                </UnstyledButton>
              )}
              {collapsed ? (
                // When collapsed, show all items (no collapse)
                section.items.map((item) => (
                  <NavbarLink
                    item={item}
                    key={item.label}
                    onClick={toggle}
                    disabled={item.disabled}
                    collapsed={collapsed}
                  />
                ))
              ) : (
                <Collapse expanded={openSections[section.id] ?? true}>
                  {section.items.map((item) => (
                    <NavbarLink
                      item={item}
                      key={item.label}
                      onClick={toggle}
                      disabled={item.disabled}
                      collapsed={collapsed}
                      badge={item.path === "/app/setup" ? setupBadge : null}
                    />
                  ))}
                </Collapse>
              )}
            </div>
          ))}
        </div>
      </ScrollArea>
    </AppShell.Navbar>
  );
}

export default Navbar;
